import type { SyncRule } from '../schemas/sync-schedule.js';

// Reiner Zeitplan-Evaluator — keine Abhängigkeiten, deterministisch über
// (rules, lastRunAt, now). Genutzt vom Worker (isDue) und der Web-Vorschau
// (nextRuns). Alle Berechnungen in LOKALZEIT: der Worker-Container läuft mit
// TZ=Europe/Berlin, der Browser in der Nutzer-Zeitzone (für DE deckungsgleich).

// Standard-Karenz, wenn es noch keinen letzten Lauf gibt: nur Feuer-Zeitpunkte
// innerhalb dieses Fensters vor "now" lösen beim ersten Tick aus (kein
// Nachholen längst vergangener Zeitpunkte auf einer frischen DB).
const DEFAULT_GRACE_MS = 90_000;

// Maximale Anzahl Tage, die nextRuns vorausschaut, bevor es aufgibt.
const MAX_LOOKAHEAD_DAYS = 31;

function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':');
  return Number(h) * 60 + Number(m);
}

function startOfDay(d: Date, dayOffset = 0): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dayOffset);
}

function atMinutes(day: Date, minutes: number): Date {
  return new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    Math.floor(minutes / 60),
    minutes % 60,
    0,
    0,
  );
}

// Sortierte Minuten-im-Tag, zu denen die Regel an diesem Kalendertag feuert.
function fireMinutesOnDay(rule: SyncRule, day: Date): number[] {
  const weekday = day.getDay();
  switch (rule.type) {
    case 'daily_at':
      return [...new Set(rule.times.map(parseTimeToMinutes))].sort((a, b) => a - b);
    case 'interval_window': {
      if (rule.days && !rule.days.includes(weekday)) return [];
      const from = parseTimeToMinutes(rule.from);
      const to = parseTimeToMinutes(rule.to);
      if (from > to) return [];
      const out: number[] = [];
      for (let m = from; m <= to; m += rule.everyMinutes) out.push(m);
      return out;
    }
    case 'every_n_hours': {
      const anchor = rule.anchor ? parseTimeToMinutes(rule.anchor) : 0;
      const step = rule.n * 60;
      const out: number[] = [];
      for (let m = anchor; m < 24 * 60; m += step) out.push(m);
      return out;
    }
    case 'weekday_at':
      return [
        ...new Set(
          rule.entries.filter((e) => e.weekday === weekday).map((e) => parseTimeToMinutes(e.time)),
        ),
      ].sort((a, b) => a - b);
    default:
      return [];
  }
}

function fireInstantsOnDay(rule: SyncRule, day: Date): Date[] {
  return fireMinutesOnDay(rule, day).map((m) => atMinutes(day, m));
}

/** Jüngster Feuer-Zeitpunkt der Regel, der ≤ now liegt (oder null). */
export function previousFireTime(rule: SyncRule, now: Date): Date | null {
  let best: Date | null = null;
  // Heute und gestern reichen: alle Regeltypen feuern innerhalb eines
  // Kalendertags, gestern deckt den Sprung kurz nach Mitternacht ab.
  for (const offset of [0, -1]) {
    const day = startOfDay(now, offset);
    for (const inst of fireInstantsOnDay(rule, day)) {
      if (inst.getTime() <= now.getTime() && (best === null || inst.getTime() > best.getTime())) {
        best = inst;
      }
    }
  }
  return best;
}

/**
 * Ist seit dem letzten Lauf ein Feuer-Zeitpunkt verstrichen?
 * lastRunAt = Start des jüngsten Auto-Laufs (aus sync_runs) oder null.
 */
export function isDue(
  rules: SyncRule[],
  lastRunAt: Date | null,
  now: Date,
  graceMs: number = DEFAULT_GRACE_MS,
): boolean {
  const lowerBound = lastRunAt ? lastRunAt.getTime() : now.getTime() - graceMs;
  return rules.some((rule) => {
    const prev = previousFireTime(rule, now);
    return prev !== null && prev.getTime() > lowerBound;
  });
}

/** Die nächsten `count` Feuer-Zeitpunkte nach `from` (für die UI-Vorschau). */
export function nextRuns(rules: SyncRule[], from: Date, count: number): Date[] {
  const out: Date[] = [];
  for (let offset = 0; offset <= MAX_LOOKAHEAD_DAYS && out.length < count; offset++) {
    const day = startOfDay(from, offset);
    const instants: Date[] = [];
    for (const rule of rules) instants.push(...fireInstantsOnDay(rule, day));
    instants.sort((a, b) => a.getTime() - b.getTime());
    for (const inst of instants) {
      if (out.length >= count) break;
      if (inst.getTime() > from.getTime() && !out.some((d) => d.getTime() === inst.getTime())) {
        out.push(inst);
      }
    }
  }
  return out;
}
